import { HttpClient, HttpContext, HttpEvent, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { BranchStateService } from './branch-state.service';

interface IDomainUrl {
    type: string;
    value: {
        __alpha_env__: string;
    };
}
type IApiOptions = {
    headers?:
        | HttpHeaders
        | {
              [header: string]: string | string[];
          };
    context?: HttpContext;
    observe?: string;
    params?:
        | HttpParams
        | {
              [param: string]: string | number | boolean | ReadonlyArray<string | number | boolean>;
          };
    reportProgress?: boolean;
    responseType?: string;
    withCredentials?: boolean;
};
@Injectable({
    providedIn: 'root',
})
export class AlphaHttpClient {
    private httpClient = inject(HttpClient);
    private branchStateService = inject(BranchStateService);

    private baseUrl = window.environments.ALPHA_UI_MAKE_REQUEST_URL;
    private apiAuth = {
        authType: 'client',
        ids_url: {
            type: 'environment',
            value: {
                __alpha_env__: 'IDS_URL',
            },
        },
        path: '/token',
        method: 'post',
        grant_type: {
            type: 'string',
            value: 'client_credentials',
        },
        headers: [
            {
                key: 'Content-Type',
                value: 'application/x-www-form-urlencoded',
            },
        ],
        body: [
            {
                key: 'client_secret',
                type: 'environment',
                value: {
                    __alpha_env__: 'IDS_CLIENT_SECRET',
                },
            },
            {
                key: 'client_id',
                type: 'environment',
                value: {
                    __alpha_env__: 'IDS_CLIENT_ID',
                },
            },
        ],
    };
    private DEFAULT_ALPHA_SERVICES = [
        'ALPHA_UI_CONFIG_SERVICE_URL',
        'ALPHA_CONFIG_SERVICE_URL',
        'ALPHA_CASE_SERVICE_URL',
        'ALPHA_UI_CASE_SERVICE_URL',
        'ALPHA_MODULES_SERVICE_URL',
        'ALPHA_UI_MODULE_SERVICE_URL',
    ];

    get<T>(domainUrl: string | IDomainUrl, path: string, options?: IApiOptions): Observable<HttpEvent<T>> {
        const apiOptions = this.getDefaultApiOptions(domainUrl, path, 'get', options);
        return this.httpClient.post<T>(`${this.baseUrl}?${path}`, apiOptions, {
            ...(options as any),
        });
    }

    post<T>(domainUrl: string | IDomainUrl, path: string, body: any, options?: IApiOptions): Observable<T> {
        const apiOptions = this.getDefaultApiOptions(domainUrl, path, 'post', options, body);
        return this.httpClient.post<T>(`${this.baseUrl}?${path}`, apiOptions, {
            headers: {
                ...((options?.headers as any) || {}),
                'Content-Type': 'application/json',
            },
        });
    }

    put<T>(domainUrl: string | IDomainUrl, path: string, body: any, options?: IApiOptions): Observable<T> {
        const apiOptions = this.getDefaultApiOptions(domainUrl, path, 'put', options, body);
        return this.httpClient.post<T>(`${this.baseUrl}?${path}`, apiOptions, {
            headers: {
                ...((options?.headers as any) || {}),
                'Content-Type': 'application/json',
            },
        });
    }
    patch<T>(domainUrl: string | IDomainUrl, path: string, body: any, options?: IApiOptions): Observable<T> {
        const apiOptions = this.getDefaultApiOptions(domainUrl, path, 'patch', options, body);
        return this.httpClient.post<T>(`${this.baseUrl}?${path}`, apiOptions, {
            headers: {
                'Content-Type': 'application/json',
            },
        });
    }

    delete<T>(domainUrl: string | IDomainUrl, path: string, options?: IApiOptions): Observable<T> {
        const apiOptions = this.getDefaultApiOptions(domainUrl, path, 'delete', options);
        return this.httpClient.post<T>(`${this.baseUrl}?${path}`, apiOptions, {
            headers: {
                'Content-Type': 'application/json',
            },
        });
    }

    private getDefaultApiOptions(
        domainUrl: string | IDomainUrl,
        path: string,
        method: string,
        options?: IApiOptions,
        body?: any,
    ) {
        const skipAlphaAuth = window.environments.SKIP_ALPHA_AUTH === 'true';
        const isAlphaURL = this.isAlphaURL(domainUrl);

        // Get branch from URL query param if present
        const queryParams: Record<string, string | number | boolean | ReadonlyArray<string | number | boolean>> = {
            ...(options?.params || {}),
        };
        const branch = this.branchStateService.getBranch();
        if (branch && isAlphaURL) {
            queryParams['branch'] = branch;
        }

        const apiOptions: any = {
            headers: options?.headers || {},
            method: method,
            body: body || {},
            url: domainUrl,
            path: path,
            queryParams,
        };

        if (!(skipAlphaAuth && isAlphaURL)) {
            apiOptions.apiAuth = this.apiAuth;
        }

        if (window.environments.ENABLE_CUSTOM_HEADERS === 'true') {
            try {
                const headers = JSON.parse(window.environments.CUSTOM_HEADERS);
                apiOptions.headers = {
                    ...apiOptions.headers,
                    ...headers,
                };
            } catch (error) {
                console.error('Error parsing custom headers:', error);
            }
        }
        return apiOptions;
    }

    isAlphaURL(url: string | IDomainUrl) {
        if (typeof url === 'string') {
            return false;
        }
        let ALPHA_SERVICE_URLS: string[];
        try {
            ALPHA_SERVICE_URLS = JSON.parse(window.environments.ALPHA_SERVICES || '[]');
            if (!Array.isArray(ALPHA_SERVICE_URLS)) {
                throw new Error('Parsed value is not an array');
            }
        } catch (error) {
            console.error('Error parsing ALPHA_SERVICES from environment:', error);
            ALPHA_SERVICE_URLS = ['ALPHA_CONFIG_SERVICE_URL', 'ALPHA_CASE_SERVICE_URL', 'ALPHA_UI_MODULE_SERVICE_URL'];
        }
        ALPHA_SERVICE_URLS = Array.from(new Set([...this.DEFAULT_ALPHA_SERVICES, ...ALPHA_SERVICE_URLS]));
        return url.type === 'environment' && ALPHA_SERVICE_URLS.includes(url.value.__alpha_env__);
    }
}
